import React, { useState, useRef, useEffect, useCallback } from "react";
import cx from "classnames";
import { MentionOption, MentionTextareaProps, TriggerConfig } from "./types";

export type {
  MentionOption,
  MentionTextareaProps,
  TriggerConfig,
} from "./types";

export function MentionTextarea({
  value,
  onChange,
  triggers,
  placeholder = "Type to mention...",
  rows = 4,
  className,
  disabled = false,
  maxLength,
  onAdd,
  onRemove,
}: MentionTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);

  const [showDropdown, setShowDropdown] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionStartPos, setMentionStartPos] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [currentTrigger, setCurrentTrigger] = useState<TriggerConfig | null>(
    null
  );
  const [filteredOptions, setFilteredOptions] = useState<MentionOption[]>([]);
  const [mentions, setMentions] = useState<
    Array<{
      start: number;
      end: number;
      text: string;
      triggerChar: string;
      displayText: string;
      option?: MentionOption;
    }>
  >([]);
  const [internalValue, setInternalValue] = useState("");

  // Convert full format to display format for internal use
  const convertToDisplayFormat = useCallback(
    (text: string) => {
      let result = text;

      for (const trigger of triggers) {
        const regex = new RegExp(
          `\\${trigger.triggerChar}\\[([^\\]]+)\\]\\(([^\\)]+)\\)`,
          "g"
        );
        result = result.replace(regex, `${trigger.triggerChar}$1`);
      }

      return result;
    },
    [triggers]
  );

  // Convert display format to full format for external use
  const convertToFullFormat = useCallback(
    (displayText: string) => {
      let fullFormatValue = displayText;

      // Replace simple mentions with full format where we have matching options
      for (const trigger of triggers) {
        const displayRegex = new RegExp(
          `\\${trigger.triggerChar}([^\\s${trigger.triggerChar}]+)`,
          "g"
        );

        fullFormatValue = fullFormatValue.replace(
          displayRegex,
          (match, mentionValue) => {
            const option = trigger.options.find(
              (opt) => opt.value === mentionValue
            );
            if (option) {
              return `${trigger.triggerChar}[${option.value}](${option.id})`;
            }
            return match; // Keep as is if no matching option
          }
        );
      }

      return fullFormatValue;
    },
    [triggers]
  );

  // Update internal value when external value changes
  useEffect(() => {
    setInternalValue(convertToDisplayFormat(value));
  }, [value, convertToDisplayFormat]);

  // Parse mentions from display text for overlay positioning
  const parseMentions = useCallback(
    (displayText: string) => {
      const foundMentions: Array<{
        start: number;
        end: number;
        text: string;
        triggerChar: string;
        displayText: string;
        option?: MentionOption;
      }> = [];

      for (const trigger of triggers) {
        // Match simple format in display text: @username, #hashtag
        const displayRegex = new RegExp(
          `\\${trigger.triggerChar}([^\\s${trigger.triggerChar}]+)`,
          "g"
        );
        let match: RegExpExecArray | null;

        while ((match = displayRegex.exec(displayText)) !== null) {
          const mentionValue = match[1];
          // Find the option by value from the original full text
          const option = trigger.options.find(
            (opt) => opt.value === mentionValue
          );

          foundMentions.push({
            start: match.index,
            end: match.index + match[0].length,
            text: match[0],
            triggerChar: trigger.triggerChar,
            displayText: match[0],
            option: option,
          });
        }
      }

      // Sort by position in text
      return foundMentions.sort((a, b) => a.start - b.start);
    },
    [triggers]
  );

  // Track previous mentions for comparison
  const prevMentionsRef = useRef<typeof mentions>([]);

  // Update mentions when internal value changes
  useEffect(() => {
    const newMentions = parseMentions(internalValue);

    // Check for removed mentions if onRemove callback is provided
    if (onRemove) {
      const previousMentionOptions = prevMentionsRef.current
        .filter((m) => m.option)
        .map((m) => ({ option: m.option!, triggerChar: m.triggerChar }));

      const currentMentionOptions = newMentions
        .filter((m) => m.option)
        .map((m) => ({ option: m.option!, triggerChar: m.triggerChar }));

      // Find removed mentions by comparing option IDs
      previousMentionOptions.forEach(({ option, triggerChar }) => {
        const stillExists = currentMentionOptions.some(
          (current) =>
            current.option.id === option.id &&
            current.triggerChar === triggerChar
        );
        if (!stillExists) {
          onRemove(option, triggerChar);
        }
      });
    }

    // Update refs and state
    prevMentionsRef.current = newMentions;
    setMentions(newMentions);
  }, [internalValue, parseMentions, onRemove]);

  // Filter options based on mention query
  useEffect(() => {
    if (mentionQuery && currentTrigger) {
      const filtered = currentTrigger.options.filter(
        (option: MentionOption) =>
          option.label.toLowerCase().includes(mentionQuery.toLowerCase()) ||
          option.value.toLowerCase().includes(mentionQuery.toLowerCase())
      );
      setFilteredOptions(filtered);
      setSelectedIndex(0);
    } else if (currentTrigger) {
      setFilteredOptions(currentTrigger.options);
      setSelectedIndex(0);
    } else {
      setFilteredOptions([]);
      setSelectedIndex(0);
    }
  }, [mentionQuery, currentTrigger]);

  // Call onMention callback when query changes
  useEffect(() => {
    if (currentTrigger?.onMention && mentionQuery) {
      currentTrigger.onMention(mentionQuery, currentTrigger.triggerChar);
    }
  }, [mentionQuery, currentTrigger]);

  // Ensure onChange is called even when typing mentions (with debounce)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (showDropdown && currentTrigger) {
        // Convert current internal value to full format and call onChange
        const fullFormatValue = convertToFullFormat(internalValue);
        onChange(fullFormatValue);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [
    internalValue,
    showDropdown,
    currentTrigger,
    convertToFullFormat,
    onChange,
  ]);

  const handleTextareaChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newDisplayValue = e.target.value;
      const cursorPosition = e.target.selectionStart;

      // Update internal value for display
      setInternalValue(newDisplayValue);

      // Check for mention trigger for each configured trigger
      const textBeforeCursor = newDisplayValue.substring(0, cursorPosition);
      let foundTrigger = false;

      for (const trigger of triggers) {
        const mentionMatch = textBeforeCursor.match(
          new RegExp(`\\${trigger.triggerChar}([^\\s${trigger.triggerChar}]*)$`)
        );

        if (mentionMatch) {
          const query = mentionMatch[1];
          const startPos = cursorPosition - mentionMatch[0].length;

          setMentionQuery(query);
          setMentionStartPos(startPos);
          setCurrentTrigger(trigger);
          setShowDropdown(true);
          foundTrigger = true;
          break; // Found a match, stop checking other triggers
        }
      }

      if (!foundTrigger) {
        // No triggers found - convert display value back to full format for external onChange
        const fullFormatValue = convertToFullFormat(newDisplayValue);
        onChange(fullFormatValue);
        setShowDropdown(false);
        setMentionQuery("");
        setMentionStartPos(0);
        setCurrentTrigger(null);
      }
    },
    [onChange, triggers, convertToFullFormat]
  );

  const insertMention = useCallback(
    (option: MentionOption) => {
      if (!textareaRef.current || !currentTrigger) return;

      const textarea = textareaRef.current;
      const beforeMention = internalValue.substring(0, mentionStartPos);
      const afterCursor = internalValue.substring(textarea.selectionStart);

      // For display: simple format
      const displayMentionText = `${currentTrigger.triggerChar}${option.value}`;
      const newDisplayValue = beforeMention + displayMentionText + afterCursor;
      const newCursorPosition =
        beforeMention.length + displayMentionText.length;

      // For external value: full format
      const fullMentionText = `${currentTrigger.triggerChar}[${option.value}](${option.id})`;

      // Convert display value to full format for external onChange
      let fullFormatValue = newDisplayValue;

      // Replace the newly inserted mention with full format
      const displayMentionRegex = new RegExp(
        `\\${currentTrigger.triggerChar}${option.value.replace(
          /[.*+?^${}()|[\]\\]/g,
          "\\$&"
        )}(?![\\w])`
      );
      fullFormatValue = fullFormatValue.replace(
        displayMentionRegex,
        fullMentionText
      );

      setInternalValue(newDisplayValue);
      onChange(fullFormatValue);
      setShowDropdown(false);
      setMentionQuery("");
      setCurrentTrigger(null);

      // Call onAdd callback if provided
      if (onAdd) {
        onAdd(option, currentTrigger.triggerChar);
      }

      // Set cursor position after the mention
      setTimeout(() => {
        if (textarea) {
          textarea.focus();
          textarea.setSelectionRange(newCursorPosition, newCursorPosition);
        }
      }, 0);
    },
    [internalValue, mentionStartPos, currentTrigger, onChange, onAdd]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!showDropdown || filteredOptions.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < filteredOptions.length - 1 ? prev + 1 : 0
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : filteredOptions.length - 1
          );
          break;
        case "Enter":
          e.preventDefault();
          if (filteredOptions[selectedIndex]) {
            insertMention(filteredOptions[selectedIndex]);
          }
          break;
        case "Escape":
          setShowDropdown(false);
          setMentionQuery("");
          break;
      }
    },
    [showDropdown, filteredOptions, selectedIndex, insertMention]
  );

  const handleOptionClick = useCallback(
    (option: MentionOption) => {
      insertMention(option);
    },
    [insertMention]
  );

  const handleBlur = useCallback(() => {
    // Ensure output is correctly formatted when focus is lost
    const fullFormatValue = convertToFullFormat(internalValue);

    onChange(fullFormatValue);
    setShowDropdown(false);
    setMentionQuery("");
    setCurrentTrigger(null);
  }, [internalValue, onChange, convertToFullFormat]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        textareaRef.current &&
        !textareaRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Render text with highlighted mentions
  const renderHighlightedText = () => {
    if (!internalValue) {
      return (
        <span className="whitespace-pre-wrap break-words text-base-content/40">
          {placeholder}
        </span>
      );
    }

    if (mentions.length === 0) {
      return (
        <span className="whitespace-pre-wrap break-words">{internalValue}</span>
      );
    }

    const parts = [];
    let lastIndex = 0;

    mentions.forEach((mention, index) => {
      // Add text before mention
      if (lastIndex < mention.start) {
        parts.push(
          <span
            key={`text-${index}`}
            className="whitespace-pre-wrap break-words"
          >
            {internalValue.substring(lastIndex, mention.start)}
          </span>
        );
      }

      // Add mention as badge
      parts.push(
        <span
          key={`mention-${index}`}
          className={cx("", mention.option ? "bg-primary" : "bg-base-300")}
          title={
            mention.option
              ? mention.option.label
              : `Unknown ${mention.triggerChar} mention`
          }
        >
          {mention.displayText}
        </span>
      );

      lastIndex = mention.end;
    });

    // Add remaining text
    if (lastIndex < internalValue.length) {
      parts.push(
        <span key="text-end" className="whitespace-pre-wrap break-words">
          {internalValue.substring(lastIndex)}
        </span>
      );
    }

    return <>{parts}</>;
  };

  return (
    <div className="relative w-full">
      {/* Textarea */}
      <textarea
        ref={textareaRef}
        value={internalValue}
        onChange={handleTextareaChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        maxLength={maxLength}
        className={cx(
          "textarea textarea-bordered w-full resize-y",
          "relative z-10 bg-transparent",
          className
        )}
        style={{
          color: "transparent",
          caretColor: "black",
        }}
      />

      {/* Highlight overlay */}
      <div
        ref={highlightRef}
        className={cx(
          "absolute inset-0 pointer-events-none z-0",
          "textarea textarea-bordered w-full resize-y overflow-hidden",
          "whitespace-pre-wrap break-words"
        )}
        style={{
          padding: "8px 12px", // Match textarea padding
          fontSize: "inherit",
          fontFamily: "inherit",
          lineHeight: "inherit",
          border: "2px solid transparent",
        }}
      >
        {renderHighlightedText()}
      </div>

      {showDropdown && filteredOptions.length > 0 && currentTrigger && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full max-w-xs bg-base-100 border border-base-300 rounded-box shadow-lg max-h-48 overflow-y-auto"
          style={{
            top: "100%",
            left: 0,
            marginTop: "0.25rem",
          }}
        >
          {filteredOptions.map((option, index) => (
            <div
              key={option.id}
              className={cx(
                "px-4 py-2 cursor-pointer hover:bg-base-200 flex items-center gap-2",
                index === selectedIndex && "bg-primary text-primary-content"
              )}
              onClick={() => handleOptionClick(option)}
            >
              <span className="text-sm opacity-60">
                {currentTrigger.triggerChar}
              </span>
              <div className="flex flex-col">
                <span className="text-sm font-medium">{option.label}</span>
                {option.label !== option.value && (
                  <span className="text-xs opacity-70">{option.value}</span>
                )}
              </div>
            </div>
          ))}

          {filteredOptions.length === 0 && mentionQuery && (
            <div className="px-4 py-2 text-sm text-base-content/50">
              No matches found for "{mentionQuery}"
            </div>
          )}
        </div>
      )}

      {maxLength && (
        <div className="text-right text-xs text-base-content/60 mt-1">
          {value.length}/{maxLength}
        </div>
      )}
    </div>
  );
}
